import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DisplaySong } from '@/types/displayHistory';
import SpotifyPreviewPlayer from '@/components/shared/SpotifyPreviewPlayer';
import {
  Music,
  Download,
  Clock,
  Youtube,
  ExternalLink,
  Star,
  Play,
  Headphones
} from 'lucide-react';

interface SongCardProps {
  song: DisplaySong;
  onPlayPreview?: (song: DisplaySong) => void;
  showSequenceLink?: boolean;
}

const SongCard: React.FC<SongCardProps> = ({
  song,
  onPlayPreview,
  showSequenceLink = true
}) => {
  const [showSpotifyPlayer, setShowSpotifyPlayer] = useState(false);
  const hasVideo = !!song.video_url;
  const hasSequence = !!song.sequence;
  const isReused = !!song.reused_from;
  const hasSpotifyPreview = !!song.spotify_preview_url;

  return (
    <Card className="overflow-hidden hover:shadow-lg transition-all duration-200 group">
      <CardContent className="p-0">
        <div className="flex flex-col sm:flex-row">
          {/* Album Art / Thumbnail */}
          <div className="relative w-full sm:w-32 h-32 sm:h-auto bg-gradient-to-br from-primary/10 to-primary/5 flex-shrink-0">
            {song.album_cover_url ? (
              <img
                src={song.album_cover_url}
                alt={`${song.title} album cover`}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Music className="h-12 w-12 text-muted-foreground/30" />
              </div>
            )}

            {/* Play overlay */}
            {hasVideo && onPlayPreview && (
              <div
                className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer flex items-center justify-center"
                onClick={() => onPlayPreview(song)}
              >
                <div className="bg-white/90 rounded-full p-3">
                  <Play className="h-6 w-6 text-primary fill-primary" />
                </div>
              </div>
            )}

            {/* Video indicator badge */}
            {hasVideo && (
              <div className="absolute top-2 right-2">
                <Badge variant="secondary" className="bg-black/60 text-white text-xs">
                  <Youtube className="h-3 w-3 mr-1" />
                  Video
                </Badge>
              </div>
            )}
          </div>

          {/* Song Info */}
          <div className="flex-1 p-4 flex flex-col justify-between min-w-0">
            <div className="space-y-2">
              {/* Title and Artist */}
              <div>
                <h3 className="font-semibold text-base leading-tight truncate group-hover:text-primary transition-colors">
                  {song.title}
                </h3>
                <p className="text-sm text-muted-foreground truncate">
                  {song.artist}
                </p>
              </div>

              {/* Metadata row */}
              <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                {/* Duration */}
                {song.duration && (
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {song.duration}
                  </span>
                )}

                {/* Genre */}
                {song.genre && (
                  <Badge variant="outline" className="text-xs">
                    {song.genre}
                  </Badge>
                )}

                {/* Year badge */}
                {isReused ? (
                  <Badge variant="outline" className="text-xs">
                    Reused from {song.year_introduced}
                  </Badge>
                ) : (
                  <Badge variant="secondary" className="text-xs">
                    New in {song.year_introduced}
                  </Badge>
                )}

                {/* Rating */}
                {song.average_rating && song.rating_count && song.rating_count > 0 && (
                  <span className="flex items-center gap-1">
                    <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                    <span>{song.average_rating.toFixed(1)}</span>
                    <span className="text-muted-foreground">({song.rating_count})</span>
                  </span>
                )}

                {/* Views */}
                {song.views_count > 0 && (
                  <span className="text-muted-foreground">
                    {song.views_count.toLocaleString()} views
                  </span>
                )}
              </div>
            </div>

            {/* Spotify Preview Player */}
            {hasSpotifyPreview && showSpotifyPlayer && song.spotify_preview_url && (
              <div className="mt-3 pt-3 border-t">
                <SpotifyPreviewPlayer
                  previewUrl={song.spotify_preview_url}
                  trackName={song.title}
                  artistName={song.artist}
                  albumCoverUrl={song.album_cover_url || undefined}
                  compact={true}
                />
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex items-center gap-2 mt-3">
              {/* Spotify Preview Button */}
              {hasSpotifyPreview && !showSpotifyPlayer && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowSpotifyPlayer(true)}
                  className="flex-shrink-0"
                >
                  <Headphones className="h-3.5 w-3.5 mr-1.5" />
                  Preview
                </Button>
              )}

              {/* Sequence Download Button */}
              {showSequenceLink && hasSequence && song.sequence && (
                <Link to={`/sequence/${song.sequence.id}`} className="flex-1">
                  <Button size="sm" variant="default" className="w-full">
                    <Download className="h-3.5 w-3.5 mr-1.5" />
                    {song.sequence.price && song.sequence.price > 0 ? (
                      <span>${song.sequence.price.toFixed(2)}</span>
                    ) : (
                      <span>Free Sequence</span>
                    )}
                  </Button>
                </Link>
              )}

              {/* Video Link */}
              {hasVideo && song.video_url && (
                <Button
                  size="sm"
                  variant="outline"
                  asChild
                  className="flex-shrink-0"
                >
                  <a
                    href={song.video_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Youtube className="h-3.5 w-3.5 mr-1.5" />
                    Watch
                  </a>
                </Button>
              )}

              {/* No Sequence Available */}
              {showSequenceLink && !hasSequence && (
                <Badge variant="outline" className="bg-muted flex-1 justify-center py-1.5">
                  No sequence available
                </Badge>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default SongCard;
